import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/apiClient';

const hasToken = () => !!localStorage.getItem('fr_token');

/* ─────────────── SUMMARY ─────────────── */
export function useTransactionSummary(period = 'week') {
  return useQuery({
    queryKey: ['summary', period],
    queryFn: () => apiClient.get(`/transactions/summary?period=${period}`).then(r => r.data),
    enabled: hasToken(),
  });
}

export function useFinancialHealth(period = 'month') {
  return useQuery({
    queryKey: ['financial-health', period],
    queryFn: () => apiClient.get(`/transactions/financial-health?period=${period}`).then(r => r.data),
    enabled: hasToken(),
  });
}

/* ─────────────── TRANSACTIONS ─────────────── */
export function useTransactions(params = {}) {
  const query = new URLSearchParams(params).toString();
  return useQuery({
    queryKey: ['transactions', params],
    queryFn: () => apiClient.get(`/transactions${query ? '?' + query : ''}`).then(r => r.data),
    enabled: hasToken(),
  });
}

export function useCreateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/transactions', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['summary'] });
      qc.invalidateQueries({ queryKey: ['financial-health'] });
    },
  });
}

export function useUpdateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => apiClient.put(`/transactions/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['summary'] });
      qc.invalidateQueries({ queryKey: ['financial-health'] });
    },
  });
}

export function useDeleteTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/transactions/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['summary'] });
      qc.invalidateQueries({ queryKey: ['financial-health'] });
    },
  });
}

/* ─────────────── CATEGORIES ─────────────── */
export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => apiClient.get('/categories').then(r => r.data),
    enabled: hasToken(),
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/categories', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => apiClient.put(`/categories/${id}`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/categories/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categories'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
    },
  });
}

/* ─────────────── BUDGETS ─────────────── */
export function useBudgets() {
  return useQuery({
    queryKey: ['budgets'],
    queryFn: () => apiClient.get('/budgets').then(r => r.data),
    enabled: hasToken(),
  });
}

export function useBudgetHealth() {
  return useQuery({
    queryKey: ['budget-health'],
    queryFn: () => apiClient.get('/budgets/health').then(r => r.data),
    enabled: hasToken(),
  });
}

export function useCreateBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data) => apiClient.post('/budgets', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['budgets'] });
      qc.invalidateQueries({ queryKey: ['budget-health'] });
    },
  });
}

export function useUpdateBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }) => apiClient.put(`/budgets/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['budgets'] });
      qc.invalidateQueries({ queryKey: ['budget-health'] });
    },
  });
}

export function useDeleteBudget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.delete(`/budgets/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['budgets'] });
      qc.invalidateQueries({ queryKey: ['budget-health'] });
    },
  });
}

/* ─────────────── ROASTS ─────────────── */
export function useRoast(weekStart) {
  return useQuery({
    queryKey: ['roast', weekStart],
    queryFn: () => apiClient.get(`/roasts?week_start=${weekStart}`).then(r => r.data),
    enabled: hasToken() && !!weekStart,
    retry: false, // 404 = no roast, don't retry
  });
}

export function useRoastHistory() {
  return useQuery({
    queryKey: ['roast-history'],
    queryFn: () => apiClient.get('/roasts/history').then(r => r.data),
    enabled: hasToken(),
  });
}

export function useGenerateRoast() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params = {}) => apiClient.post(`/roasts/generate${params?.force ? '?force=true' : ''}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roast'] });
      qc.invalidateQueries({ queryKey: ['roast-history'] });
      qc.invalidateQueries({ queryKey: ['insights'] });
    },
  });
}

/* ─────────────── INSIGHTS ─────────────── */
export function useInsights(weekStart) {
  return useQuery({
    queryKey: ['insights', weekStart],
    queryFn: () => apiClient.get(`/insights?week_start=${weekStart}`).then(r => r.data),
    enabled: hasToken() && !!weekStart,
  });
}
